// src/modules/app-views/crm/pages/Kanban/modals/NewOpportunityModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { UserAddOutlined, CameraOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import WebcamCaptureModal from 'layouts/SharedComponents/WebcamCaptureModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN MODAL COMPONENT
// *******************************************************************************
const CrmNewOpportunityModal = ({ show, setShow, afterClose, boardUid, columnUid, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Holds the File objects ready for AI extraction
  const cardFilesRef = useRef([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Controlled file list for the Upload.Dragger (shared with camera capture)
  const [fileList, setFileList] = useState([]);
  // Whether there are files attached — controls button label and whether AI runs
  const [hasFiles, setHasFiles] = useState(false);
  // Fullscreen loading overlay shown during AI processing
  const [processing, setProcessing] = useState(false);
  // Tracks which step is active so the camera button only shows on Step 1
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // Controls the webcam/native-camera modal
  const [webcamOpen, setWebcamOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Called whenever the file list changes (upload or remove)
  const handleFileListChange = newList => {
    const trimmed = newList.slice(-1);
    setFileList(trimmed);
    const files = trimmed.map(f => f.originFileObj).filter(Boolean);
    cardFilesRef.current = files;
    setHasFiles(files.length > 0);
  };

  // Camera capture — creates a synthetic upload file item from the captured File
  const handleCameraCapture = file => {
    setWebcamOpen(false);
    const newItem = {
      uid: `webcam-${Date.now()}`,
      name: file.name || `card-${Date.now()}.jpg`,
      status: 'done',
      originFileObj: file,
    };
    handleFileListChange([newItem]);
  };

  // onNext: runs only when files are attached (button labeled "PROCESSAR")
  const handleNext = async ({ form }) => {
    const files = cardFilesRef.current || [];
    if (files.length === 0) return;

    setProcessing(true);
    try {
      // Run image compression and GPS capture in parallel to save time
      const [images, location] = await Promise.all([
        Promise.all(files.map(fileToBase64)),
        getGpsLocation(),
      ]);

      const [response] = await axiosService.post({
        url: API.AI.EXTRACT_CARD,
        data: { images, ...(location ? { location } : {}) },
        returnAsObject: true,
      });

      // Backend returns { success, data: {...} }; axiosService may unwrap or not
      const data = response?.data || response || {};
      const patch = {};
      if (data.name) patch.name = data.name;
      if (data.company) patch.company = data.company;
      if (data.jobTitleRaw) patch.jobTitleRaw = data.jobTitleRaw;
      if (data.businessAreaRaw) patch.businessAreaRaw = data.businessAreaRaw;
      if (data.opportunity) patch.opportunity = data.opportunity;
      if (data.note) patch.note = data.note;
      if (data.phone) patch.phone = { value: data.phone };
      if (data.email) patch.email = { value: data.email };
      if (data.instagram) patch.instagram = data.instagram;
      if (data.website) patch.website = data.website;

      if (Object.keys(patch).length > 0) {
        form.setFieldsValue(patch);
      }
      // Return undefined → GenericModal advances to Step 2
    } catch (err) {
      console.error('[NewOpportunityModal] AI card extraction error:', err);
      appService.message({ flag: 'e', text: 'error' });
      return false; // Stay on Step 1
    } finally {
      setProcessing(false);
    }
  };

  // onFinish: final save on Step 2
  const handleFinish = async ({ form }) => {
    const values = form?.getFieldsValue(true) || {};

    // Phone OR email — at least one required
    const phone = values?.phone?.value?.trim() || '';
    const email = values?.email?.value?.trim() || '';
    if (!phone && !email) {
      appService.message({ flag: 'w', text: 'phone_or_email_required' });
      return false;
    }

    // Find the initial column of the selected board
    const selectedBoardUid = values.boardUid;
    const boards = storedParameters?.modules?.crm?.boards || [];
    const selectedBoard = boards.find(b => b.uid === selectedBoardUid);
    const sortedColumns = [...(selectedBoard?.columns || [])].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
    const initialColumn = sortedColumns.find(c => c.initial) || sortedColumns[0] || null;
    const targetColumnUid = initialColumn?.uid || columnUid || null;

    const hasCompany = !!(values.company?.trim());
    let entityId = null;
    let personContactId = null;

    if (hasCompany) {
      // 1a. Create person entity (profile: contact, linked to company later)
      const personPayload = buildPersonPayload(values, false);
      const [createdPerson] = await axiosService.post({
        url: API.ENTITIES.URL.replace(':_id?', ''),
        data: personPayload,
        returnAsObject: true,
      });
      const personId = createdPerson?._id || null;
      personContactId = personId;

      // 1b. Create company entity (profile: opportunity) linking person as contact
      const companyPayload = buildCompanyPayload(values, personId);
      const [createdCompany] = await axiosService.post({
        url: API.ENTITIES.URL.replace(':_id?', ''),
        data: companyPayload,
        returnAsObject: true,
      });
      entityId = createdCompany?._id || null;
    } else {
      // 1. Create person entity (profile: opportunity — the lead itself)
      const personPayload = buildPersonPayload(values, true);
      const [createdPerson] = await axiosService.post({
        url: API.ENTITIES.URL.replace(':_id?', ''),
        data: personPayload,
        returnAsObject: true,
      });
      entityId = createdPerson?._id || null;
      personContactId = entityId;
    }

    // 2. Create CRM opportunity
    const consultantId = values.consultantId || undefined;

    const crmPayload = {
      status: 'backlog',
      opportunityType: values.opportunityType || 'sale',
      opportunity: (values.opportunity || '').trim(),
      note: (values.note || '').trim() || undefined,
      boardUid: selectedBoardUid,
      columnUid: targetColumnUid,
      entityId,
      personContactId,
      ...(consultantId ? { consultantId, consultantIds: [consultantId] } : {}),
      // Custom form fields (Step 3) — only include when present
      ...(values.formData ? { formData: values.formData } : {}),
    };

    await axiosService.post({
      url: API.CRM.URL.replace(':_id?', ''),
      data: crmPayload,
      returnAsObject: true,
    });

    // 3. Refresh kanban board so the new card appears immediately
    window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));

    appService.message({ flag: 's', text: 'saved_successfully' });
    onSuccess?.();
    // Return undefined — GenericModal closes automatically
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Resolve the selected board and its initial column to check for custom form fields
  const boards = storedParameters?.modules?.crm?.boards || [];
  const selectedBoard = boards.find(b => b.uid === boardUid);
  const sortedColumns = [...(selectedBoard?.columns || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const initialColumn = sortedColumns.find(c => c.initial) || sortedColumns[0] || null;
  const initialColumnUid = initialColumn?.uid || columnUid || null;
  const boardFormFields = selectedBoard?.formFields || [];
  const hasFormFields = boardFormFields.length > 0;

  const steps = useMemo(
    () => {
      const base = [
        {
          stepTitle: 'business_card',
          // Button label is reactive: "Próximo" when no files, "PROCESSAR" when files attached
          nextButtonText: hasFiles ? 'PROCESSAR' : 'next',
          // onNext only runs when files are present; undefined = GenericModal advances naturally
          onNext: hasFiles ? handleNext : undefined,
          onEnter: () => setCurrentStepIndex(0),
          content: () => (
            <Step1
              fileList={fileList}
              onFileListChange={handleFileListChange}
              processing={processing}
            />
          ),
        },
        {
          stepTitle: 'contact',
          validationFields: [['phone', 'value'], ['email', 'value'], 'name', 'opportunity', 'opportunityType'],
          onEnter: ({ form }) => {
            setCurrentStepIndex(1);
            // Focus the first empty field so the user can immediately continue filling
            setTimeout(() => focusFirstEmptyField(form), 150);
          },
          content: () => <Step2 />,
        },
      ];

      // Conditional Step 3: custom form fields (only when board has them)
      if (hasFormFields) {
        // Build validationFields for required custom fields in the initial column
        const requiredFormFields = boardFormFields
          .filter(f => (f.requiredInColumns || []).includes(initialColumnUid))
          .map(f => ['formData', f.fieldName]);

        base.push({
          stepTitle: 'data',
          validationFields: requiredFormFields,
          onEnter: () => setCurrentStepIndex(2),
          content: () => (
            <Step3 formFields={boardFormFields} columnUid={initialColumnUid} />
          ),
        });
      }

      return base;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasFiles, fileList, processing, hasFormFields, boardFormFields, initialColumnUid]
  );

  // Camera button — only shown on Step 1
  const customLeftButtons = useMemo(
    () =>
      currentStepIndex === 0
        ? [
            {
              tooltip: 'take_photo',
              icon: <CameraOutlined />,
              onClick: () => setWebcamOpen(true),
            },
          ]
        : [],
    [currentStepIndex]
  );

  const initialValues = { boardUid, opportunityType: 'sale' };
  const requiredFields = ['boardUid', 'name', 'opportunity', 'opportunityType'];

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <GenericModal
        show={show}
        setShow={setShow}
        afterClose={afterClose}
        title={translateX('new_opportunity')}
        icon={<UserAddOutlined />}
        steps={steps}
        onFinish={handleFinish}
        initialValues={initialValues}
        requiredFields={requiredFields}
        width={540}
        customLeftButtons={customLeftButtons}
      />

      {/* Webcam / native camera for business card capture */}
      <WebcamCaptureModal
        open={webcamOpen}
        onCancel={() => setWebcamOpen(false)}
        onCapture={handleCameraCapture}
        cam="fullScreen"
      />
    </>
  );
};

// *******************************************************************************
// EXPORT (named — for reuse as React component)
// *******************************************************************************
export { CrmNewOpportunityModal };

// *******************************************************************************
// IMPERATIVE SHOW FUNCTION (default export)
// *******************************************************************************
function showKanbanQuickAddModal({ boardUid, columnUid, onSuccess } = {}) {
  const container = document.createElement('div');
  container.id = 'kanban-quick-add-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);

  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <CrmNewOpportunityModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          boardUid={boardUid}
          columnUid={columnUid}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
}

export default showKanbanQuickAddModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Builds the person entity payload.
 * @param {Object} values - Form values
 * @param {boolean} isOpportunity - true when person IS the opportunity entity (no company);
 *                                  false when person is a contact under a company
 */
function buildPersonPayload(values, isOpportunity) {
  const payload = {
    name: (values.name || '').trim(),
    entityType: 'person',
    entityProfile: [isOpportunity ? 'opportunity' : 'contact'],
  };

  if (values.jobTitleRaw?.trim()) {
    payload.jobTitleRaw = values.jobTitleRaw.trim();
  }

  if (values.phone?.value?.trim()) {
    payload.phone = {
      value: values.phone.value.trim(),
      countryCode: values.phone.countryCode || 'BR',
      type: values.phone.type || 'mobile',
    };
  }

  if (values.email?.value?.trim()) {
    payload.email = {
      value: values.email.value.trim(),
      countryCode: 'BR',
    };
  }

  // When person is the opportunity (no company), attach web/social contacts directly
  if (isOpportunity) {
    const addToSetContacts = [];

    if (values.website?.trim()) {
      addToSetContacts.push({ contactType: 'website', value: values.website.trim(), countryCode: 'BR' });
    }

    if (values.instagram?.trim()) {
      addToSetContacts.push({
        contactType: 'instagram',
        value: values.instagram.replace(/^@/, '').trim(),
        countryCode: 'BR',
      });
    }

    if (addToSetContacts.length > 0) {
      payload.addToSet = { contacts: addToSetContacts };
    }
  }

  return payload;
}

/**
 * Builds the company entity payload.
 * Links the person as a 'person' contact inside the company's contacts.
 * @param {Object} values - Form values
 * @param {string|null} personId - _id of the already-created person entity
 */
function buildCompanyPayload(values, personId) {
  const payload = {
    name: values.company.trim(),
    entityType: 'company',
    entityProfile: ['opportunity'],
  };

  if (values.businessAreaRaw?.trim()) {
    payload.businessAreaRaw = values.businessAreaRaw.trim();
  }

  const addToSetContacts = [];

  // Link person as contact of the company
  if (personId) {
    addToSetContacts.push({
      contactType: 'person',
      linkedEntityId: personId,
      value: (values.name || '').trim(),
      countryCode: 'BR',
    });
  }

  if (values.website?.trim()) {
    addToSetContacts.push({ contactType: 'website', value: values.website.trim(), countryCode: 'BR' });
  }

  if (values.instagram?.trim()) {
    addToSetContacts.push({
      contactType: 'instagram',
      value: values.instagram.replace(/^@/, '').trim(),
      countryCode: 'BR',
    });
  }

  if (addToSetContacts.length > 0) {
    payload.addToSet = { contacts: addToSetContacts };
  }

  return payload;
}

// Focus the first empty/blank field in Step 2 so the user can start typing immediately.
// Fields are checked in display order; phone and email use nested array name paths.
function focusFirstEmptyField(form) {
  const FIELD_ORDER = [
    ['phone', 'value'],
    'name',
    'opportunity',
    'company',
    'businessAreaRaw',
    'jobTitleRaw',
    ['email', 'value'],
  ];
  for (const field of FIELD_ORDER) {
    const value = form.getFieldValue(field);
    const isEmpty = !value || (typeof value === 'string' && !value.trim());
    if (isEmpty) {
      form.getFieldInstance(field)?.focus?.();
      break;
    }
  }
}

// Capture GPS location silently (returns null if unavailable or denied).
function getGpsLocation() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, maximumAge: 60000 }
    );
  });
}

// Compress and resize image before base64 encoding.
// Phone photos can be 3-8 MB; resizing to max 1200px keeps quality sufficient for
// Claude vision while keeping the payload well under the 10 MB body-parser limit.
function fileToBase64(file) {
  const MAX_SIDE = 1200; // px — enough resolution for business card text recognition
  const QUALITY = 0.88; // JPEG quality
  const mediaType = 'image/jpeg'; // always output JPEG for consistency

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = evt => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        // Calculate new dimensions keeping aspect ratio
        let { width, height } = img;
        if (width > MAX_SIDE || height > MAX_SIDE) {
          if (width >= height) {
            height = Math.round((height * MAX_SIDE) / width);
            width = MAX_SIDE;
          } else {
            width = Math.round((width * MAX_SIDE) / height);
            height = MAX_SIDE;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL(mediaType, QUALITY);
        const commaIdx = dataUrl.indexOf(',');
        resolve({ base64: dataUrl.slice(commaIdx + 1), mediaType });
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
  });
}
